/**
 * Diálogo de importación desde planilla, en tres pasos.
 *
 *   1. **Subir**   — el archivo (.csv o .xlsx), o bajarse la plantilla vacía.
 *   2. **Mapear**  — qué columna va a qué campo, y qué hacer con los repetidos.
 *   3. **Revisar** — la prueba en seco fila por fila, y recién ahí importar.
 *
 * Es genérico como todo el renderer: no sabe qué modelo está importando. Los
 * campos, sus etiquetas, los candidatos a columna clave y el mapeo propuesto los
 * manda el backend a partir del `ModelSpec`. Agregar un módulo no agrega una
 * línea acá.
 *
 * Tres decisiones que conviene conocer antes de tocarlo:
 *
 * 1. **No se puede importar sin haber visto la prueba en seco.** El botón del
 *    paso 3 no existe hasta que la validación volvió. Es la razón de ser del
 *    diálogo: nadie carga 200 filas a ciegas, y una barra de progreso que
 *    termina en "se importaron 137 de 200" no le sirve a nadie.
 * 2. **El archivo se vuelve a subir en cada paso.** El backend no guarda estado
 *    entre pasos y el navegador ya tiene el `File`; ver la nota en `api.ts`.
 * 3. **Los errores se muestran tal como los redactó el backend.** Están escritos
 *    en castellano y nombran la columna del usuario. Reescribirlos acá sería
 *    tener dos vocabularios que se desincronizan.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/shell/primitives';
import { notify } from '@/components/shell/toast';
import { BotonBloqueado } from '@/views/MotivoBloqueo';
import { api } from '@/lib/api';
import type {
  ImportDuplicatePolicy,
  ImportPreview,
  ImportResult,
  ImportRow,
  ModelSpec,
} from '@/lib/types';

type Paso = 'subir' | 'mapear' | 'revisar';

export interface ImportDialogProps {
  spec: ModelSpec;
  open: boolean;
  onClose: () => void;
  /** Se llama cuando la importación escribió algo: la lista tiene que refrescar. */
  onImported: () => void;
}

/** Etiqueta y tono de cada acción de fila. Es el semáforo de la revisión. */
const ACCIONES: Record<ImportRow['action'], { texto: string; clase: string }> = {
  crear: { texto: 'Se crea', clase: 'bg-success-soft text-success border-success/30' },
  actualizar: { texto: 'Se actualiza', clase: 'bg-accent-soft text-accent border-accent/30' },
  omitir: { texto: 'Se omite', clase: 'bg-surface-raised text-fg-muted border-line' },
  error: { texto: 'Error', clase: 'bg-danger-soft text-danger border-danger/30' },
};

const POLITICAS: { valor: ImportDuplicatePolicy; titulo: string; detalle: string }[] = [
  {
    valor: 'crear',
    titulo: 'Crear todo',
    detalle: 'Si alguna fila ya existe, la importación lo marca como error.',
  },
  {
    valor: 'omitir',
    titulo: 'Crear sólo los nuevos',
    detalle: 'Los que ya están se dejan como están.',
  },
  {
    valor: 'actualizar',
    titulo: 'Actualizar los que ya están',
    detalle: 'Y crear los que falten. Una celda vacía no borra lo que ya está cargado.',
  },
];

export function ImportDialog({ spec, open, onClose, onImported }: ImportDialogProps) {
  const panel = useRef<HTMLDivElement>(null);
  const focoPrevio = useRef<HTMLElement | null>(null);
  const entradaArchivo = useRef<HTMLInputElement>(null);

  const [paso, setPaso] = useState<Paso>('subir');
  const [archivo, setArchivo] = useState<File | null>(null);
  const [vista, setVista] = useState<ImportPreview | null>(null);
  const [mapeo, setMapeo] = useState<Record<number, string>>({});
  const [clave, setClave] = useState<string>('');
  const [politica, setPolitica] = useState<ImportDuplicatePolicy>('crear');
  const [todoONada, setTodoONada] = useState(true);
  const [resultado, setResultado] = useState<ImportResult | null>(null);
  const [importado, setImportado] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cada apertura arranca de cero: un mapeo heredado de la importación anterior
  // sobre otro archivo es la forma más rápida de cargar basura.
  useEffect(() => {
    if (!open) return;
    setPaso('subir');
    setArchivo(null);
    setVista(null);
    setMapeo({});
    setClave('');
    setPolitica('crear');
    setTodoONada(true);
    setResultado(null);
    setImportado(false);
    setError(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    focoPrevio.current = document.activeElement as HTMLElement | null;

    const alTeclear = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') {
        evento.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', alTeclear, true);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const temporizador = window.setTimeout(() => {
      panel.current?.querySelector<HTMLElement>('[data-foco-inicial]')?.focus();
    }, 0);

    return () => {
      document.removeEventListener('keydown', alTeclear, true);
      document.body.style.overflow = overflow;
      window.clearTimeout(temporizador);
      focoPrevio.current?.focus();
    };
  }, [open, onClose]);

  /** Campos obligatorios que ninguna columna está alimentando. */
  const obligatoriosSinMapear = useMemo(() => {
    if (vista === null) return [];
    const asignados = new Set(Object.values(mapeo));
    return vista.fields.filter((campo) => campo.required && !asignados.has(campo.name));
  }, [vista, mapeo]);

  const opciones = useMemo(
    () => ({
      mapping: Object.fromEntries(
        Object.entries(mapeo).filter(([, campo]) => campo !== ''),
      ) as Record<string, string>,
      key_field: clave === '' ? null : clave,
      on_duplicate: politica,
      atomic: todoONada,
    }),
    [mapeo, clave, politica, todoONada],
  );

  const mensajeDeError = (excepcion: unknown): string =>
    excepcion instanceof Error ? excepcion.message : 'No se pudo completar la operación.';

  const subir = async (elegido: File): Promise<void> => {
    setOcupado(true);
    setError(null);
    try {
      const datos = await api.importPreview(spec.model, elegido);
      setArchivo(elegido);
      setVista(datos);
      setMapeo(
        Object.fromEntries(
          datos.columns.filter((c) => c.field !== null).map((c) => [c.index, c.field as string]),
        ),
      );
      setPaso('mapear');
    } catch (excepcion) {
      setError(mensajeDeError(excepcion));
    } finally {
      setOcupado(false);
    }
  };

  const validar = async (): Promise<void> => {
    if (archivo === null) return;
    setOcupado(true);
    setError(null);
    try {
      setResultado(await api.importValidate(spec.model, archivo, opciones));
      setPaso('revisar');
    } catch (excepcion) {
      setError(mensajeDeError(excepcion));
    } finally {
      setOcupado(false);
    }
  };

  const importar = async (): Promise<void> => {
    if (archivo === null) return;
    setOcupado(true);
    setError(null);
    try {
      const salida = await api.importRun(spec.model, archivo, opciones);
      setResultado(salida);
      setImportado(salida.committed);
      if (salida.committed) {
        onImported();
        notify.success(salida.message);
      } else {
        notify.error('No se importó nada', salida.message);
      }
    } catch (excepcion) {
      setError(mensajeDeError(excepcion));
    } finally {
      setOcupado(false);
    }
  };

  const bajarPlantilla = async (formato: 'csv' | 'xlsx'): Promise<void> => {
    try {
      const { blob, filename } = await api.downloadImportTemplate(spec.model, formato);
      const url = URL.createObjectURL(blob);
      const enlace = document.createElement('a');
      enlace.href = url;
      enlace.download = filename;
      enlace.click();
      URL.revokeObjectURL(url);
    } catch (excepcion) {
      notify.error('No se pudo bajar la plantilla', mensajeDeError(excepcion));
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-2 sm:items-center sm:p-4">
      <div className="absolute inset-0 bg-fg/30 animate-fade-in" onClick={onClose} aria-hidden="true" />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="importar-titulo"
        className="relative flex max-h-[92vh] w-full max-w-4xl flex-col rounded-xl border border-line bg-surface shadow-lg animate-slide-up"
      >
        <header className="shrink-0 border-b border-line px-5 py-4">
          <h2 id="importar-titulo" className="text-lg font-semibold">
            Importar {spec.label_plural.toLowerCase()} desde una planilla
          </h2>
          <Pasos actual={paso} />
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {error !== null && (
            <p
              role="alert"
              className="mb-4 rounded-lg border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger"
            >
              {error}
            </p>
          )}

          {paso === 'subir' && (
            <PasoSubir
              spec={spec}
              ocupado={ocupado}
              entrada={entradaArchivo}
              onArchivo={(elegido) => {
                void subir(elegido);
              }}
              onPlantilla={(formato) => {
                void bajarPlantilla(formato);
              }}
            />
          )}

          {paso === 'mapear' && vista !== null && (
            <PasoMapear
              vista={vista}
              mapeo={mapeo}
              onMapeo={setMapeo}
              clave={clave}
              onClave={setClave}
              politica={politica}
              onPolitica={setPolitica}
              todoONada={todoONada}
              onTodoONada={setTodoONada}
              obligatoriosSinMapear={obligatoriosSinMapear.map((c) => c.label)}
            />
          )}

          {paso === 'revisar' && resultado !== null && (
            <PasoRevisar resultado={resultado} importado={importado} />
          )}
        </div>

        <footer className="flex shrink-0 flex-col-reverse gap-2 border-t border-line px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-xs text-fg-faint">
            {vista !== null && paso !== 'subir' && (
              <>
                {archivo?.name}
                {' · '}
                {vista.row_count === 1 ? '1 fila' : `${vista.row_count} filas`}
                {vista.sheet !== null && ` · hoja «${vista.sheet}»`}
              </>
            )}
          </div>

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="ghost" onClick={onClose}>
              {importado ? 'Cerrar' : 'Cancelar'}
            </Button>

            {paso === 'mapear' && (
              <>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setPaso('subir');
                  }}
                >
                  Volver
                </Button>
                {Object.values(mapeo).filter((c) => c !== '').length === 0 ? (
                  <BotonBloqueado
                    variant="primary"
                    label="Revisar sin importar"
                    motivo="Todavía no asignaste ninguna columna del archivo a un campo del sistema. Elegí arriba, para cada columna, a qué campo va: sin eso no hay nada que revisar."
                  />
                ) : (
                  <Button
                    variant="primary"
                    loading={ocupado}
                    onClick={() => {
                      void validar();
                    }}
                  >
                    Revisar sin importar
                  </Button>
                )}
              </>
            )}

            {paso === 'revisar' && !importado && (
              <>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setPaso('mapear');
                  }}
                >
                  Volver a mapear
                </Button>
                {(() => {
                  const cuantas = (resultado?.to_create ?? 0) + (resultado?.to_update ?? 0);
                  const etiqueta = `Importar ${cuantas === 1 ? '1 fila' : `${cuantas} filas`}`;
                  /*
                    Tres motivos distintos frenaban este botón y ninguno se
                    decía. El peor era el del medio: el paso de revisión lista
                    los errores de las filas, pero nada contaba que fueran ELLOS
                    los que frenan el botón ni que destildando «todo o nada» se
                    destraba. Alguien con una fila mala en un archivo de
                    trescientas se quedaba mirando un botón gris.
                  */
                  const motivo =
                    resultado === null
                      ? 'Todavía no se revisó el archivo. Apretá «Revisar sin importar» y volvé.'
                      : todoONada && !resultado.ok
                        ? `Hay ${resultado.failed === 1 ? '1 fila con problemas' : `${resultado.failed} filas con problemas`} y está marcado «todo o nada»: así, o entra el archivo entero o no entra nada. Corregí esas filas en el archivo y volvé a subirlo, o destildá «todo o nada» para que entren las que sí están bien.`
                        : cuantas === 0
                          ? 'No hay ninguna fila para crear ni para actualizar: con este mapeo y esta clave, el archivo no aporta nada nuevo.'
                          : null;
                  return motivo === null ? (
                    <Button
                      variant="primary"
                      loading={ocupado}
                      onClick={() => {
                        void importar();
                      }}
                    >
                      {etiqueta}
                    </Button>
                  ) : (
                    <BotonBloqueado variant="primary" label={etiqueta} motivo={motivo} />
                  );
                })()}
              </>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cabecera de pasos
// ---------------------------------------------------------------------------
const ETIQUETAS_PASO: { clave: Paso; texto: string }[] = [
  { clave: 'subir', texto: 'Subir el archivo' },
  { clave: 'mapear', texto: 'Mapear las columnas' },
  { clave: 'revisar', texto: 'Revisar y confirmar' },
];

function Pasos({ actual }: { actual: Paso }) {
  const indiceActual = ETIQUETAS_PASO.findIndex((p) => p.clave === actual);
  return (
    <ol className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-fg-faint">
      {ETIQUETAS_PASO.map((paso, indice) => {
        const activo = indice === indiceActual;
        const hecho = indice < indiceActual;
        return (
          <li key={paso.clave} className="flex items-center gap-2">
            {indice > 0 && (
              <span aria-hidden="true" className="text-line-strong">
                ›
              </span>
            )}
            <span
              aria-current={activo ? 'step' : undefined}
              className={
                activo
                  ? 'font-medium text-accent'
                  : hecho
                    ? 'text-fg-muted'
                    : 'text-fg-faint'
              }
            >
              {indice + 1}. {paso.texto}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

// ---------------------------------------------------------------------------
// Paso 1 — subir
// ---------------------------------------------------------------------------
function PasoSubir({
  spec,
  ocupado,
  entrada,
  onArchivo,
  onPlantilla,
}: {
  spec: ModelSpec;
  ocupado: boolean;
  entrada: React.RefObject<HTMLInputElement>;
  onArchivo: (archivo: File) => void;
  onPlantilla: (formato: 'csv' | 'xlsx') => void;
}) {
  const [encima, setEncima] = useState(false);

  return (
    <div className="space-y-5">
      <div
        onDragOver={(evento) => {
          evento.preventDefault();
          setEncima(true);
        }}
        onDragLeave={() => {
          setEncima(false);
        }}
        onDrop={(evento) => {
          evento.preventDefault();
          setEncima(false);
          const soltado = evento.dataTransfer.files[0];
          if (soltado !== undefined) onArchivo(soltado);
        }}
        className={`rounded-xl border-2 border-dashed p-8 text-center transition-colors ${
          encima ? 'border-accent bg-accent-soft' : 'border-line bg-app-subtle'
        }`}
      >
        <p className="text-sm font-medium">Arrastrá acá la planilla, o elegila</p>
        <p className="mt-1 text-xs text-fg-faint">
          Se aceptan .csv y .xlsx. La primera fila tiene que ser la de los encabezados.
        </p>
        <input
          ref={entrada}
          type="file"
          accept=".csv,.xlsx,.xlsm,text/csv"
          className="sr-only"
          onChange={(evento) => {
            const elegido = evento.target.files?.[0];
            if (elegido !== undefined) onArchivo(elegido);
            evento.target.value = '';
          }}
        />
        <Button
          variant="primary"
          className="mt-4"
          loading={ocupado}
          data-foco-inicial
          onClick={() => {
            entrada.current?.click();
          }}
        >
          Elegir archivo
        </Button>
      </div>

      <div className="rounded-lg border border-line bg-app-subtle p-4">
        <h3 className="text-sm font-medium">¿Todavía no tenés la planilla?</h3>
        <p className="mt-1 text-xs text-fg-muted">
          Bajate la plantilla vacía de {spec.label_plural.toLowerCase()} y mandásela a quien tenga
          los datos. Los encabezados ya vienen con los nombres que el sistema reconoce solo.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            size="sm"
            onClick={() => {
              onPlantilla('csv');
            }}
          >
            Descargar plantilla (.csv)
          </Button>
          <Button
            size="sm"
            onClick={() => {
              onPlantilla('xlsx');
            }}
          >
            Descargar plantilla (.xlsx)
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Paso 2 — mapear
// ---------------------------------------------------------------------------
const CLASE_SELECT =
  'h-9 w-full rounded-md border border-line bg-surface px-2 text-sm outline-none transition-colors ' +
  'focus:border-accent focus:ring-2 focus:ring-accent/25';

function PasoMapear({
  vista,
  mapeo,
  onMapeo,
  clave,
  onClave,
  politica,
  onPolitica,
  todoONada,
  onTodoONada,
  obligatoriosSinMapear,
}: {
  vista: ImportPreview;
  mapeo: Record<number, string>;
  onMapeo: (mapeo: Record<number, string>) => void;
  clave: string;
  onClave: (clave: string) => void;
  politica: ImportDuplicatePolicy;
  onPolitica: (politica: ImportDuplicatePolicy) => void;
  todoONada: boolean;
  onTodoONada: (valor: boolean) => void;
  obligatoriosSinMapear: string[];
}) {
  const usados = new Set(Object.values(mapeo).filter((c) => c !== ''));
  const candidatos = vista.fields.filter((campo) => vista.key_candidates.includes(campo.name));
  // Sólo se puede usar como clave un campo que además esté mapeado: si no, no
  // hay de dónde sacar el valor con el que comparar.
  const clavesDisponibles = candidatos.filter((campo) => usados.has(campo.name));

  return (
    <div className="space-y-5">
      {vista.truncated && (
        <p className="rounded-lg border border-warning/30 bg-warning-soft px-3 py-2 text-sm text-warning">
          El archivo tiene más filas de las que el importador procesa de una vez. Se leyeron las
          primeras; partilo en varias planillas.
        </p>
      )}

      {obligatoriosSinMapear.length > 0 && (
        <p className="rounded-lg border border-warning/30 bg-warning-soft px-3 py-2 text-sm text-warning">
          Sin mapear quedan campos obligatorios: {obligatoriosSinMapear.join(', ')}. Si no los
          completa un valor por defecto, la revisión los va a marcar fila por fila.
        </p>
      )}

      <div className="overflow-hidden rounded-lg border border-line">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[38rem] border-collapse text-sm">
            <caption className="sr-only">Columnas de la planilla y el campo al que van</caption>
            <thead>
              <tr className="border-b border-line bg-app-subtle text-left">
                <th scope="col" className="px-3 py-2 font-medium text-fg-muted">
                  Columna de la planilla
                </th>
                <th scope="col" className="px-3 py-2 font-medium text-fg-muted">
                  Primeros valores
                </th>
                <th scope="col" className="w-64 px-3 py-2 font-medium text-fg-muted">
                  Campo del sistema
                </th>
              </tr>
            </thead>
            <tbody>
              {vista.columns.map((columna) => {
                const elegido = mapeo[columna.index] ?? '';
                const muestra = vista.sample_rows
                  .map((fila) => fila[columna.index])
                  .filter((valor): valor is string => valor !== null && valor !== undefined)
                  .slice(0, 3);
                return (
                  <tr key={columna.index} className="border-b border-line last:border-0">
                    <td className="px-3 py-2 align-top">
                      <span className="font-medium">{columna.header}</span>
                      {columna.match === 'aproximado' && elegido !== '' && (
                        <span className="ml-2 rounded-full border border-warning/30 bg-warning-soft px-1.5 py-0.5 text-2xs text-warning">
                          revisá
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top text-xs text-fg-muted">
                      {muestra.length === 0 ? (
                        <span className="text-fg-faint">(vacía)</span>
                      ) : (
                        muestra.join(' · ')
                      )}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <select
                        aria-label={`Campo para la columna ${columna.header}`}
                        value={elegido}
                        onChange={(evento) => {
                          const copia = { ...mapeo };
                          if (evento.target.value === '') delete copia[columna.index];
                          else copia[columna.index] = evento.target.value;
                          onMapeo(copia);
                          if (clave !== '' && !Object.values(copia).includes(clave)) onClave('');
                        }}
                        className={CLASE_SELECT}
                      >
                        <option value="">— no importar —</option>
                        {vista.fields.map((campo) => (
                          <option
                            key={campo.name}
                            value={campo.name}
                            disabled={campo.name !== elegido && usados.has(campo.name)}
                          >
                            {campo.label}
                            {campo.required ? ' *' : ''}
                            {/* Adentro de un `<option>` no hay dónde poner un
                                globo: el único canal es la etiqueta, así que la
                                etiqueta lo dice. Antes eran opciones grises sin
                                explicación y parecían campos prohibidos. */}
                            {campo.name !== elegido && usados.has(campo.name)
                              ? ' — ya lo está cargando otra columna'
                              : ''}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <fieldset className="rounded-lg border border-line p-4">
        <legend className="px-1 text-sm font-medium">¿Y si alguno ya está cargado?</legend>

        <label className="mt-2 block text-sm">
          <span className="mb-1.5 block text-fg-muted">
            Columna que dice cuál es cuál (la clave)
          </span>
          <select
            value={clave}
            onChange={(evento) => {
              onClave(evento.target.value);
              if (evento.target.value === '') onPolitica('crear');
            }}
            className={`${CLASE_SELECT} max-w-sm`}
          >
            <option value="">— sin clave: todo se crea —</option>
            {clavesDisponibles.map((campo) => (
              <option key={campo.name} value={campo.name}>
                {campo.label}
              </option>
            ))}
          </select>
        </label>

        <div className="mt-3 space-y-2">
          {POLITICAS.map((opcion) => (
            <label
              key={opcion.valor}
              className={`flex items-start gap-2.5 text-sm ${
                clave === '' && opcion.valor !== 'crear' ? 'opacity-50' : ''
              }`}
            >
              <input
                type="radio"
                name="politica-duplicados"
                value={opcion.valor}
                checked={politica === opcion.valor}
                disabled={clave === '' && opcion.valor !== 'crear'}
                onChange={() => {
                  onPolitica(opcion.valor);
                }}
                className="mt-0.5 h-4 w-4 shrink-0 accent-[rgb(var(--accent))]"
              />
              <span>
                <span className="font-medium">{opcion.titulo}</span>
                <span className="mt-0.5 block text-xs text-fg-faint">{opcion.detalle}</span>
              </span>
            </label>
          ))}
        </div>

        <label className="mt-4 flex items-start gap-2.5 border-t border-line pt-3 text-sm">
          <input
            type="checkbox"
            checked={todoONada}
            onChange={(evento) => {
              onTodoONada(evento.target.checked);
            }}
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-line accent-[rgb(var(--accent))]"
          />
          <span>
            <span className="font-medium">Todo o nada</span>
            <span className="mt-0.5 block text-xs text-fg-faint">
              Si una sola fila falla, no se importa ninguna. Sin esto se guarda lo que está bien y
              queda un padrón a medias que hay que reconciliar a mano.
            </span>
          </span>
        </label>
      </fieldset>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Paso 3 — revisar
// ---------------------------------------------------------------------------
function PasoRevisar({ resultado, importado }: { resultado: ImportResult; importado: boolean }) {
  const [soloErrores, setSoloErrores] = useState(resultado.failed > 0);
  const filas = soloErrores ? resultado.rows.filter((f) => f.action === 'error') : resultado.rows;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Contador etiqueta="Se crean" valor={resultado.to_create} tono="success" />
        <Contador etiqueta="Se actualizan" valor={resultado.to_update} tono="accent" />
        <Contador etiqueta="Se omiten" valor={resultado.skipped} tono="neutral" />
        <Contador etiqueta="Con error" valor={resultado.failed} tono="danger" />
      </div>

      <p
        role="status"
        className={`rounded-lg border px-3 py-2 text-sm ${
          importado
            ? 'border-success/30 bg-success-soft text-success'
            : resultado.failed > 0
              ? 'border-danger/30 bg-danger-soft text-danger'
              : 'border-line bg-app-subtle text-fg-muted'
        }`}
      >
        {importado
          ? resultado.message
          : resultado.failed > 0
            ? `Todavía no se escribió nada. ${resultado.failed} ${
                resultado.failed === 1 ? 'fila tiene' : 'filas tienen'
              } errores: corregilos en la planilla y volvé a subirla.`
            : 'Todavía no se escribió nada. Revisá el detalle y confirmá para importar.'}
      </p>

      {/* Lo que la revision en seco no puede prometer se dice, no se calla. */}
      {(resultado.warnings ?? []).length > 0 && (
        <div className="rounded-lg border border-warning/30 bg-warning-soft px-3 py-2 text-sm text-warning">
          <p className="font-medium">Esto la revisión no lo pudo comprobar:</p>
          <ul className="mt-1 list-disc space-y-0.5 pl-5">
            {(resultado.warnings ?? []).map((aviso) => (
              <li key={aviso}>{aviso}</li>
            ))}
          </ul>
        </div>
      )}

      {resultado.failed > 0 && (
        <label className="flex items-center gap-2 text-sm text-fg-muted">
          <input
            type="checkbox"
            checked={soloErrores}
            onChange={(evento) => {
              setSoloErrores(evento.target.checked);
            }}
            className="h-4 w-4 rounded border-line accent-[rgb(var(--accent))]"
          />
          Ver sólo las filas con problemas
        </label>
      )}

      <div className="overflow-hidden rounded-lg border border-line">
        <div className="max-h-[38vh] overflow-y-auto">
          <table className="w-full border-collapse text-sm">
            <caption className="sr-only">Detalle fila por fila</caption>
            <thead className="sticky top-0 bg-app-subtle">
              <tr className="border-b border-line text-left">
                <th scope="col" className="w-16 px-3 py-2 font-medium text-fg-muted">
                  Fila
                </th>
                <th scope="col" className="w-32 px-3 py-2 font-medium text-fg-muted">
                  Qué pasa
                </th>
                <th scope="col" className="px-3 py-2 font-medium text-fg-muted">
                  Detalle
                </th>
              </tr>
            </thead>
            <tbody>
              {filas.map((fila) => {
                const accion = ACCIONES[fila.action];
                return (
                  <tr key={fila.number} className="border-b border-line last:border-0 align-top">
                    <td className="px-3 py-2 tabular-nums text-fg-muted">{fila.number}</td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-block rounded-full border px-2 py-0.5 text-xs ${accion.clase}`}
                      >
                        {accion.texto}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {fila.errors.length > 0 ? (
                        <ul className="space-y-0.5 text-danger">
                          {fila.errors.map((mensaje, indice) => (
                            <li key={indice}>{mensaje}</li>
                          ))}
                        </ul>
                      ) : (
                        <>
                          <span className="font-medium">{fila.label || '(sin nombre)'}</span>
                          {/* `display` y no `values`: el backend ya lo escribió
                              para una persona —el nombre del edificio, no su id—
                              y volver a formatearlo acá sería un segundo
                              vocabulario que se desincroniza con el primero. */}
                          <span className="ml-2 text-xs text-fg-faint">
                            {Object.entries(fila.display)
                              .slice(0, 4)
                              .map(([etiqueta, texto]) => `${etiqueta}: ${texto}`)
                              .join(' · ')}
                          </span>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filas.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-3 py-6 text-center text-sm text-fg-muted">
                    No hay filas para mostrar.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const TONOS_CONTADOR: Record<string, string> = {
  success: 'border-success/30 bg-success-soft text-success',
  accent: 'border-accent/30 bg-accent-soft text-accent',
  neutral: 'border-line bg-app-subtle text-fg-muted',
  danger: 'border-danger/30 bg-danger-soft text-danger',
};

function Contador({ etiqueta, valor, tono }: { etiqueta: string; valor: number; tono: string }) {
  return (
    <div className={`rounded-lg border px-3 py-2 ${TONOS_CONTADOR[tono]}`}>
      <div className="text-xl font-semibold tabular-nums">{valor}</div>
      <div className="text-xs">{etiqueta}</div>
    </div>
  );
}
